import React from 'react';
import { CheckCircle2, Circle, Loader2, AlertTriangle, Lock } from 'lucide-react';

/**
 * iter431 — Talent ordered onboarding progress tracker.
 *
 * Renders the 5-step sequence (phone → profile → ID → IDV → agreement) as a
 * vertical stepper with per-step status + the exact reason a step is blocked.
 * Purely presentational; `steps` comes from GET /api/talent/onboarding/status.
 */

const STEP_INDEX = { phone: 1, profile: 2, id_upload: 3, idv: 4, agreement: 5 };

const stepVisual = (step, isCurrent) => {
  if (step.status === 'complete') {
    return { Icon: CheckCircle2, ring: 'bg-emerald-50 text-emerald-600 ring-emerald-200' };
  }
  if (step.key === 'idv' && (step.idv_status === 'pending_review')) {
    return { Icon: Loader2, ring: 'bg-amber-50 text-amber-600 ring-amber-200', spin: true };
  }
  if (step.idv_status === 'rejected') {
    return { Icon: AlertTriangle, ring: 'bg-rose-50 text-rose-600 ring-rose-200' };
  }
  if (isCurrent) {
    return { Icon: Circle, ring: 'bg-cyan-50 text-cyan-700 ring-cyan-300' };
  }
  return { Icon: Lock, ring: 'bg-slate-50 text-slate-400 ring-slate-200' };
};

export const OnboardingProgress = ({ steps = [], currentStep, onStepAction }) => {
  const completed = steps.filter((s) => s.status === 'complete').length;
  const pct = steps.length ? Math.round((completed / steps.length) * 100) : 0;

  return (
    <div data-testid="onboarding-progress" className="w-full">
      {/* Overall bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase font-bold text-slate-500 tracking-wider">
            Onboarding progress
          </span>
          <span data-testid="onboarding-progress-pct" className="text-xs font-bold text-cyan-700">
            {completed}/{steps.length} · {pct}%
          </span>
        </div>
        <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-cyan-600 transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <ol className="space-y-3">
        {steps.map((step, i) => {
          const isCurrent = step.key === currentStep;
          const { Icon, ring, spin } = stepVisual(step, isCurrent);
          const locked = step.status !== 'complete' && !isCurrent && step.idv_status !== 'rejected';
          return (
            <li
              key={step.key}
              data-testid={`onboarding-step-${step.key}`}
              className={`flex gap-3 rounded-xl border p-3 transition-colors ${
                isCurrent ? 'border-cyan-300 bg-cyan-50/40' : 'border-slate-200 bg-white'
              }`}
            >
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ring-2 ${ring}`}>
                <Icon className={`h-5 w-5 ${spin ? 'animate-spin' : ''}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-900">
                    <span className="text-slate-400 mr-1">{STEP_INDEX[step.key]}.</span>
                    {step.label}
                  </p>
                  {step.status === 'complete' ? (
                    <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-600">Done</span>
                  ) : isCurrent ? (
                    <span className="text-[11px] font-bold uppercase tracking-wide text-cyan-700">Now</span>
                  ) : step.idv_status === 'rejected' ? (
                    <span className="text-[11px] font-bold uppercase tracking-wide text-rose-600">Action needed</span>
                  ) : (
                    <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Locked</span>
                  )}
                </div>
                {step.reason && step.status !== 'complete' && (
                  <p className="mt-0.5 text-xs text-slate-500">{step.reason}</p>
                )}
                {isCurrent && onStepAction && (
                  <button
                    data-testid={`onboarding-step-${step.key}-cta`}
                    onClick={() => onStepAction(step)}
                    className="mt-2 inline-flex items-center rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-700 transition-colors"
                  >
                    {step.status === 'complete' ? 'Review' : 'Continue'}
                  </button>
                )}
                {!isCurrent && step.idv_status === 'rejected' && onStepAction && (
                  <button
                    data-testid={`onboarding-step-${step.key}-cta`}
                    onClick={() => onStepAction(step)}
                    className="mt-2 inline-flex items-center rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 transition-colors"
                  >
                    Fix & re-upload
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default OnboardingProgress;
